/**
 * Key Rotation Management Component
 * 
 * Displays key rotation status and history for edges
 */

import { useState, useEffect } from 'react';
import { RefreshCw, Clock, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import edgeService from '../../services/edge.service';
import { useTenantStore } from '../../stores/tenantStore';
import { formatDate } from '../../utils/formatDate';
import { useSettingsStore } from '../../stores/settingsStore';
import { useToast } from '../common/ToastContainer';
import ConfirmDialog from '../common/ConfirmDialog';

interface KeyRotationHistory {
  id: number;
  edgeGuid: string;
  oldKeyFingerprint: string | null;
  newKeyFingerprint: string;
  rotatedAt: string;
  rotatedBy: string;
  status: 'success' | 'failed';
  errorMessage?: string;
}

interface CurrentKeyInfo {
  fingerprint: string;
  created_at: string;
  key_age_days: number;
}

interface KeyRotationManagementProps {
  edgeGuid: string;
}

export default function KeyRotationManagement({ edgeGuid }: KeyRotationManagementProps) {
  const toast = useToast();
  const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
  const [history, setHistory] = useState<KeyRotationHistory[]>([]);
  const [currentKey, setCurrentKey] = useState<CurrentKeyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, [edgeGuid, selectedTenantId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await edgeService.getKeyRotationHistory(edgeGuid);
      // API returns snake_case, component handles it directly
      const data = response as any;
      if (data && data.history) {
        setHistory(data.history);
      } else {
        setHistory([]);
      }
      // Get current key info (new from API)
      if (data && data.current_key) {
        setCurrentKey(data.current_key);
      } else {
        setCurrentKey(null);
      }
    } catch (error) {
      console.error('Failed to fetch rotation history:', error);
      setHistory([]);
      setCurrentKey(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRotate = () => {
    setShowRotateConfirm(true);
  };

  const confirmRotate = async () => {
    setRotating(true);
    try {
      await edgeService.rotateSSHTunnelKey(edgeGuid);
      toast.success('Key Rotation', 'Key rotation initiated');
      setShowRotateConfirm(false);

      // Refresh history after a delay
      setTimeout(fetchHistory, 2000);
    } catch (error) {
      console.error('Failed to rotate key:', error);
      toast.error('Key Rotation Failed', 'Failed to rotate key');
    } finally {
      setRotating(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'failed':
        return <XCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Clock className="w-5 h-5 text-gray-600" />;
    }
  };


  const getKeyAge = () => {
    // Use current key info if available, otherwise fall back to rotation history
    if (currentKey) {
      return `${currentKey.key_age_days} days`;
    }
    if (history.length === 0) return 'No key';

    const lastRotation = new Date(history[0].rotatedAt);
    const now = new Date();
    const days = Math.floor((now.getTime() - lastRotation.getTime()) / (1000 * 60 * 60 * 24));

    return `${days} days`;
  };

  const getKeyAgeStatus = (): 'healthy' | 'warning' | 'critical' | 'unknown' => {
    // Use current key info if available
    if (currentKey) {
      const days = currentKey.key_age_days;
      if (days > 90) return 'critical';
      if (days > 80) return 'warning';
      return 'healthy';
    }
    // Fall back to rotation history
    if (history.length === 0) return 'unknown';

    const lastRotation = new Date(history[0].rotatedAt);
    const now = new Date();
    const days = Math.floor((now.getTime() - lastRotation.getTime()) / (1000 * 60 * 60 * 24));

    if (days > 90) return 'critical';
    if (days > 80) return 'warning';
    return 'healthy';
  };

  const keyAgeStatus = getKeyAgeStatus();

  return (
    <div className="space-y-6">
      {/* Key Age Status */}
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Key Status</h3>
          <button
            onClick={handleRotate}
            disabled={rotating}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className={`w-4 h-4 ${rotating ? 'animate-spin' : ''}`} />
            Rotate Key
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <Clock className="w-8 h-8 text-gray-600" />
            <div>
              <p className="text-sm text-gray-600">Key Age</p>
              <p className="text-xl font-semibold">{getKeyAge()}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            {keyAgeStatus === 'healthy' && <CheckCircle2 className="w-8 h-8 text-green-600" />}
            {keyAgeStatus === 'warning' && <AlertTriangle className="w-8 h-8 text-orange-600" />}
            {keyAgeStatus === 'critical' && <XCircle className="w-8 h-8 text-red-600" />}
            {keyAgeStatus === 'unknown' && <Clock className="w-8 h-8 text-gray-400" />}
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-xl font-semibold capitalize">{keyAgeStatus}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <RefreshCw className="w-8 h-8 text-gray-600" />
            <div>
              <p className="text-sm text-gray-600">Rotations</p>
              <p className="text-xl font-semibold">{history.length}</p>
            </div>
          </div>
        </div>

        {keyAgeStatus === 'critical' && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              ⚠️ Key is older than 90 days. Rotation recommended for security compliance.
            </p>
          </div>
        )}

        {keyAgeStatus === 'warning' && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <p className="text-sm text-orange-800">
              ⚠️ Key is approaching 90 days. Consider rotating soon.
            </p>
          </div>
        )}

        {keyAgeStatus === 'unknown' && !currentKey && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              ℹ️ No tunnel key found. Generate a tunnel key to start tracking key age.
            </p>
          </div>
        )}

        {currentKey && keyAgeStatus === 'healthy' && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-900">Current Key Active</p>
                <div className="mt-1 text-sm text-green-800">
                  <p>Fingerprint: <span className="font-mono">{currentKey.fingerprint}</span></p>
                  <p>Created: {formatDate(currentKey.created_at, useSettingsStore.getState().getDateFormatSettings())}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Rotation History */}
      <div className="bg-white rounded-lg border shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Rotation History</h3>
          <button
            onClick={fetchHistory}
            disabled={loading}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2" />
            Loading history...
          </div>
        ) : history.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            {currentKey ? (
              <div className="mb-2">
                <p className="font-medium text-gray-900">No rotation history yet</p>
                <p className="text-sm text-gray-500 mt-1">
                  The current key (created {formatDate(currentKey.created_at, useSettingsStore.getState().getDateFormatSettings())}) has not been rotated.
                </p>
              </div>
            ) : (
              <>
                <p className="mb-2">No rotation history available</p>
                <p className="text-sm text-gray-400">History will appear after generating a tunnel key</p>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {history.map((rotation) => (
              <div
                key={rotation.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(rotation.status)}
                  <div>
                    <p className="font-medium">{formatDate(rotation.rotatedAt, useSettingsStore.getState().getDateFormatSettings())}</p>
                    <p className="text-sm text-gray-600">
                      Rotated by: {rotation.rotatedBy}
                    </p>
                    {rotation.errorMessage && (
                      <p className="text-sm text-red-600 mt-1">{rotation.errorMessage}</p>
                    )}
                  </div>
                </div>

                <div className="text-right text-sm text-gray-600">
                  <p className="font-mono">{rotation.newKeyFingerprint}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Confirm Rotate Dialog */}
      <ConfirmDialog
        isOpen={showRotateConfirm}
        onClose={() => setShowRotateConfirm(false)}
        onConfirm={confirmRotate}
        title="Rotate SSH Tunnel Key"
        message="Rotate SSH tunnel key? This will restart the tunnel."
        confirmText="Rotate"
        variant="warning"
        isLoading={rotating}
      />
    </div>
  );
}

